import { ResponsiveImage } from "@features/atoms/ResponsiveImage"
import { TOWER, THEATER } from "@features/site/Logo"
import { NewsletterForm } from "@features/site/NewsletterForm"
import { defineBlock, InnerBlocks } from "eddev/blocks"
import { AnimatePresence, motion } from "motion/react"
import { useEffect, useState } from "react"
import { cn, tv } from "tailwind-variants"

export const meta: BlockMeta = {
  title: "Holding Page",
  description: "A holding page for the site",
  conditions: {
    multiple: false,
    templates: ["template-holding-page"],
  },
}

export default defineBlock("holding-page/holding-page", (props) => {
  const styles = createStyles({})

  const [imageLoaded, setImageLoaded] = useState(false)

  const [isSubscribing, setIsSubscribing] = useState(false)

  return (
    <div className={styles.base()}>
      <div className={styles.inner()}>
        <div className={styles.left()}>
          <LogoPart delay={200}>{TOWER}</LogoPart>
          <div
            className={styles.textInner()}
            onClick={(e) => {
              if (e.target instanceof HTMLAnchorElement) {
                if (e.target.getAttribute("href") === "#") {
                  e.preventDefault()
                  setIsSubscribing(true)
                }
              }
            }}
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={isSubscribing ? "subscribing" : "default"}
                initial="hidden"
                animate="visible"
                exit="hidden"
                variants={{
                  hidden: { opacity: 0, y: 0 },
                  visible: { opacity: 1, y: 0 },
                }}
                transition={{ duration: 0.2 }}
              >
                {isSubscribing ? (
                  <div>
                    <a
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        setIsSubscribing(false)
                      }}
                      className="text-[12px]"
                      href="#"
                    >
                      &larr; Go Back
                    </a>
                    <div className="mt-5 mx-auto max-w-[400px]">
                      <NewsletterForm />
                    </div>
                  </div>
                ) : (
                  <InnerBlocks
                    allowedBlocks={["core/paragraph"]}
                    defaultBlocks={[["core/paragraph", { content: "Coming soon." }]]}
                    wrapBlock={(child, ctx) => (
                      <motion.div
                        initial="hidden"
                        animate="visible"
                        variants={{
                          hidden: { opacity: 0, y: 0 },
                          visible: { opacity: 1, y: 0 },
                        }}
                        transition={{ duration: 1, delay: 1 + ctx.index * 0.2 }}
                      >
                        {child}
                      </motion.div>
                    )}
                  />
                )}
              </motion.div>
            </AnimatePresence>
          </div>
          <LogoPart delay={200}>{THEATER}</LogoPart>
        </div>
        <div className={styles.right()}>
          <motion.div
            className="absolute inset-0 isolate"
            initial="hidden"
            animate={imageLoaded ? "visible" : "hidden"}
            variants={{
              hidden: { opacity: 0, scale: 1.05 },
              visible: { opacity: 1, scale: 1 },
            }}
            transition={{ duration: 1.5, delay: 0.5 }}
          >
            <ResponsiveImage {...props.image} max onLoad={() => setImageLoaded(true)} className="z-10" />
            <motion.div
              variants={{
                hidden: { opacity: 0 },
                visible: { opacity: 1 },
              }}
              transition={{ duration: 0.5, delay: 2 }}
              className="absolute right-5 bottom-5 text-[12px] md:text-[12px] leading-tight uppercase z-20"
              dangerouslySetInnerHTML={{ __html: props.image?.caption ?? "" }}
            />
          </motion.div>
        </div>
      </div>
    </div>
  )
})

function LogoPart(props: { delay: number; children: React.ReactNode }) {
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const timeout = setTimeout(() => {
      setVisible(true)
    }, props.delay)

    return () => clearTimeout(timeout)
  }, [props.delay])

  return (
    <motion.div initial="hidden" animate={visible ? "visible" : "hidden"} className="flex items-stretch py-7 gap-6">
      <div className="flex-1 flex flex-col justify-between items-start">
        <Line side="left" longer={false} />
        <Line side="left" longer={true} />
        <Line side="left" longer={false} />
      </div>
      <motion.div
        variants={{
          hidden: { opacity: 0, "--transitionProgress": 0 },
          visible: { opacity: 1, "--transitionProgress": 1 },
        }}
        transition={{ duration: 1.5, delay: 0.5 }}
        style={{
          // "--transitionProgress": 0.99,
          "--mask": "rgba(0,0,0, calc(pow(var(--transitionProgress), 3)))",
          maskImage: `linear-gradient(
              90deg,
              var(--mask) calc(50% - var(--transitionProgress) * 50%),
              black 50%,
              var(--mask) calc(50% + var(--transitionProgress) * 50%)
            )`,
        }}
      >
        {props.children}
      </motion.div>
      <div className="flex-1 flex flex-col justify-between items-end">
        <Line side="right" longer={false} />
        <Line side="right" longer={true} />
        <Line side="right" longer={false} />
      </div>
    </motion.div>
  )
}

function Line(props: { side: "left" | "right"; longer: boolean }) {
  return (
    <motion.div
      variants={{
        hidden: { scaleX: 0 },
        visible: { scaleX: 1 },
      }}
      transition={{ duration: 1.5, restSpeed: 0.001, restDelta: 0.001, damping: 50, type: "spring" }}
      className={cn(
        "h-[2px] bg-current w-full",
        props.longer ? "w-full" : "w-[calc(100%-1em)]",
        props.side === "left" ? "origin-left" : "origin-right",
      )}
    />
  )
}

const createStyles = tv({
  base: ["bg-[#26393A] text-[#D7FCFF]", "fixed inset-0 wp:relative wp:h-screen", "[font-family:Geist_Mono]"],
  slots: {
    inner: "relative w-full h-full flex flex-col md:flex-row",
    left: "flex-1 relative flex flex-col items-stretch justify-between",
    right: "flex-1 relative overflow-hidden max-md:aspect-[2]",
    textInner: [
      "text-center px-5 el-p:text-balance el-p:my-[0.8em]",
      "el-p:text-[12px] el-p:md:text-[16px] el-p:leading-[1.4] el-p:uppercase",
      "el-a:underline el-a:underline-offset-[0.2em] el-a:decoration-[color-mix(in_srgb,currentColor_40%,transparent)] hover:el-a:text-white hover:el-a:decoration-white el-a:transition-all",
      "[&_.holding-page-links]:flex [&_.holding-page-links]:gap-[1.2em] [&_.holding-page-links]:items-center [&_.holding-page-links]:justify-center",
    ],
  },
})
